import { GenericAlert } from "@studio3t/generic-components";
import { useId } from "react";
import type { FieldRuleRow } from "@/utils/fieldRules";
import { FieldRulesFields } from "./FieldRulesFields";
import { StepperInput } from "./StepperInput";
import {
  FieldRowEven,
  FormGroup,
  FormSection,
  HelpText,
  Label,
  SectionTitle,
} from "./styles";

export interface GeneratorFieldsProps {
  eventsPerSecond: string;
  setEventsPerSecond: (value: string) => void;
  totalEvents: string;
  setTotalEvents: (value: string) => void;
  seed: string;
  setSeed: (value: string) => void;
  rules: FieldRuleRow[];
  setRules: (rows: FieldRuleRow[]) => void;
}

/**
 * Configuration for a synthetic generator service.
 *
 * Rate, volume and seed describe the generator rather than any one pipeline,
 * which is why the engine keeps them on the service. A pipeline needing a
 * different rate declares another generator service. Field rules narrow
 * single fields of whatever schema the pipeline picks (TTTL-229).
 */
export function GeneratorFields({
  eventsPerSecond,
  setEventsPerSecond,
  totalEvents,
  setTotalEvents,
  seed,
  setSeed,
  rules,
  setRules,
}: GeneratorFieldsProps) {
  const rateId = useId();
  const totalId = useId();
  const seedId = useId();

  // Figma 16097-33747: the schema reminder leads the card, the three
  // numbers share a row, and the field rules form a ruled section of their
  // own a section gap below.
  return (
    <>
      <FormSection>
        <SectionTitle>Generator configuration</SectionTitle>

        <GenericAlert
          state="Info"
          icon
          dismissible={false}
          width="100%"
          message="A pipeline using this service must select a schema, it defines the documents to generate."
        />

        <FieldRowEven>
          <FormGroup>
            <Label htmlFor={rateId}>Events per second</Label>
            <StepperInput
              id={rateId}
              label="events per second"
              value={eventsPerSecond}
              onChange={setEventsPerSecond}
              placeholder="50"
              // The engine clamps 0 to 1 silently; the form never offers it.
              min={1}
            />
            <HelpText>
              Documents are paced at this rate, not emitted in bursts.
            </HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor={totalId}>Total events</Label>
            <StepperInput
              id={totalId}
              label="total events"
              value={totalEvents}
              onChange={setTotalEvents}
              placeholder=""
            />
            <HelpText>
              Leave empty to run until stopped. Applies per pipeline, so two
              pipelines each generate this many.
            </HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor={seedId}>Seed</Label>
            <StepperInput
              id={seedId}
              label="seed"
              value={seed}
              onChange={setSeed}
              placeholder=""
              min={0}
            />
            <HelpText>
              Leave empty for different data each run. Set a seed to repeat the
              same documents across pipelines and schemas sharing this service.
            </HelpText>
          </FormGroup>
        </FieldRowEven>
      </FormSection>

      <FieldRulesFields rows={rules} setRows={setRules} />
    </>
  );
}
